/**
 * 6.2 BaseWidget 继承体系（借鉴 Appsmith）
 *
 * 现有渲染器为 React 函数组件（runtime-renderer.tsx），BaseWidget 在此设计中
 * 作为**元数据/行为载体**：getPageView 默认经由 rendererRegistry 返回现有渲染
 * 函数产出的 React 节点（不做组件写法改造），子类只需声明类型、属性面板配置与
 * 可选属性（6.3 派生/默认/瞬态）。
 */
import * as React from "react";

import type { PropertyPaneConfig } from "@shared/types";

import type { PropertyDefinition } from "./property-types";
import { getRenderer } from "./renderer-registry";

/** 6.2 蓝图：子组件结构自动生成 */
export interface Blueprint {
  children: { type: string; props?: Record<string, unknown> }[];
}

export type WidgetProps = Record<string, unknown>;

export abstract class BaseWidget<TProps extends WidgetProps = WidgetProps> {
  /** 组件唯一标识（与 material.type 一致） */
  abstract getWidgetType(): string;

  /** 6.4 配置驱动属性面板 */
  abstract getPropertyPaneConfig(): PropertyPaneConfig[];

  /**
   * 6.2 页面渲染：返回现有渲染函数产出的 React 节点。
   * 默认实现通过 rendererRegistry 查找渲染器（运行时由 runtime-renderer 注册），
   * 子类可覆盖以返回自定义视图。
   */
  getPageView(props: TProps): React.ReactNode {
    const type = this.getWidgetType();
    const renderer = getRenderer(type);
    if (!renderer) {
      return React.createElement(
        "div",
        { className: "text-xs text-destructive" },
        `缺少渲染器：${type}`,
      );
    }
    // 仅作为元数据/预览载体：构造最小 node 形状交给现有渲染器
    return React.createElement(renderer, {
      node: { id: `preview-${type}`, type, props },
      props,
    });
  }

  /** 6.2 可选：子组件结构自动生成 */
  getBlueprint?(): Blueprint;

  /** 6.3 派生属性：表达式从其他属性计算（运行时求值，不持久化） */
  getDerivedProperties?(): Record<string, string>;

  /** 6.3 默认属性：属性面板默认值 */
  getDefaultProperties?(): Record<string, unknown>;

  /** 6.3 瞬态属性：不持久化但可用于绑定（如 isFocused） */
  getMetaProperties?(): Record<string, unknown>;

  /** 汇总 6.3 三种属性定义（供运行时构建 this 上下文） */
  propertyDefinitions(): PropertyDefinition[] {
    const defs: PropertyDefinition[] = [];

    const defaults = this.getDefaultProperties?.() ?? {};
    for (const [name, value] of Object.entries(defaults)) {
      defs.push({ kind: "default", name, value });
    }

    const metas = this.getMetaProperties?.() ?? {};
    for (const [name, initialValue] of Object.entries(metas)) {
      defs.push({ kind: "meta", name, initialValue });
    }

    const derived = this.getDerivedProperties?.() ?? {};
    for (const [name, expression] of Object.entries(derived)) {
      defs.push({ kind: "derived", name, expression, label: name });
    }

    return defs;
  }
}

/** Widget 类构造器（注册表条目） */
export type WidgetConstructor<T extends BaseWidget = BaseWidget> = new () => T;
